import { type CollectionView, EventListener, type FocusControl, View, type ViewData } from "silver";

const HTML =
`<div class="modal" data-myon-on-click="handleShadeClick">
	<div class="modal_shade" data-myon-name="shade"></div>
	<div class="modal_frames">
		<div class="modal_frame" data-myon-on-click="handleShadeClick" data-myon-template="contents">
			<div class="modal_zoom" data-myon-name="zoom">
				<div data-myon-name="contents" class="modal_contents" data-myon-on-click="handleContentsClick">
					{{ content @noescape }}
				</div>
			</div>
		</div>
	</div>
</div>`;

interface ModalViewData extends ViewData {
	contents: ModalFrameViewData[];
}

interface ModalFrameViewData extends ViewData {
	content: HTMLElement;
}

interface ModalFrameView extends View<ModalFrameViewData> {
	contents: HTMLDivElement;
}

interface ModalView extends View<ModalViewData> {
	contents: CollectionView<ModalFrameViewData, ModalFrameView>;
}

function wait(duration: number): Promise<void> {
	return new Promise((resolve) => {
		setTimeout(() => {
			resolve();
		}, duration);
	});
}

interface ModalFrameOptions {
	sourceX?: number;
	sourceY?: number;
	source?: HTMLElement;
	marginTop?: number;
	width?: number|null|undefined;
	height?: number;
	onMount?: () => any;
	dismissable: boolean;
}

interface ModalHistory<T> {
	resolve: (result: T|null) => any;
	reject: (reason: any) => any;
	element: HTMLElement;
	parent: Node|null;
	options: ModalFrameOptions;
}

export class Modal {

	public readonly onShow = EventListener.create("show");
	public readonly onClose = EventListener.create("close");

	private history: Array<ModalHistory<any>> = [];
	private emphasizeTimer: number = -1;
	private view: ModalView;

	constructor(
		private focusControl: FocusControl,
		private defaultWidth: number = 900,
	) {
		this.view = new View<ModalViewData>(HTML, {
			handleShadeClick: (e) => {
				e.stopPropagation();
				if (! this.isDismissable()) {
					this.emphasize();
				} else {
					this.back(null);
				}
			},
			handleContentsClick: (e) => {
				e.stopPropagation();
			}
		}, {
			contents: []
		}) as ModalView;

		window.addEventListener("resize", () => {
			this.updateLayout(true);
		});
	}

	public element(): HTMLElement {
		return this.view.element;
	}

	public contents(): HTMLDivElement|null {
		if (this.history.length === 0) {
			return null;
		}
		const view = this.view.contents.get(String(this.history.length - 1));
		return view.contents;
	}

	public static isChildWindow(): boolean {
		return !! (window as any).modalFrame;
	}

	public static getParentModal(): Modal {
		return (window as any).modalFrame;
	}

	private isDismissable(): boolean {
		if (this.history.length === 0) {
			throw new Error("Nothing to dismiss");
		}
		return this.history[ this.history.length - 1 ].options.dismissable;
	}

	public show<T>(element: HTMLElement, params?: Partial<ModalFrameOptions>|number): Promise<T|null> {
		const options = this.getShowOptions(params);
		this.view.element.classList.add("-push", "-active");
		this.view.element.classList.remove("-pop");
		if (!element.nodeType) {
			throw new Error("Invalid node in Modal.show");
		}
		document.body.classList.add("modal_noscroll");

		return new Promise<T>((resolve, reject) => {
			this.history.push({ resolve, reject, element, parent: element.parentNode as Node, options });
			const el = this.view.contents.add(String(this.history.length - 1), { content: element });
			this.focusControl.push(element, () => {
				if (this.isDismissable()) {
					this.back(null);
				} else {
					this.emphasize();
				}
			});
			setTimeout(() => {
				el.element.classList.add("-ready");
			});
			this.onShow.trigger({ });
			if (options.onMount) {
				options.onMount();
			}
			this.updateLayout(true, options.marginTop);

			if (options.sourceX && options.sourceY) {
				el.zoom.style.transformOrigin = `${options.sourceX}px ${options.sourceY}px`;
			}
		});
	}

	private getShowOptions(options?: Partial<ModalFrameOptions>|number): ModalFrameOptions {
		if (!options) {
			return { dismissable: true };
		}
		if (typeof options === "number") {
			return { marginTop: (options as number), dismissable: true };
		}
		const source = (options as ModalFrameOptions).source;
		if (source) {
			const rect = source.getBoundingClientRect();
			options.sourceX = (rect.left + rect.width / 2);
			options.sourceY = (rect.top + rect.height / 2);
		}
		if (! options.hasOwnProperty("dismissable")) {
			options.dismissable = true;
		}
		return options as ModalFrameOptions;
	}

	public async back<T>(results: T|null): Promise<void> {
		if (! this.history.length) {
			throw new Error("No history");
		}
		const index = String(this.history.length - 1);
		const element = this.view.contents.get(index).element;
		const state = this.history.pop() as ModalHistory<T>;

		element.classList.remove("-ready");
		await wait(300);
		this.view.contents.remove(index);
		this.focusControl.pop();
		this.view.element.classList.toggle("-active", this.history.length > 0);
		if (state.parent) {
			state.parent.appendChild(state.element);
		} else if (state.element.parentNode) {
			state.element.parentNode.removeChild(state.element);
		}
		if (this.history.length > 0) {
			this.updateLayout(true);
		} else {
			document.body.classList.remove("modal_noscroll");
		}
		state.resolve(results);
	}

	public updateLayout(force: boolean = false, marginTop?: number): void {
		if (this.history.length === 0) {
			return;
		}
		const contents = this.contents();
		if (!contents) {
			throw new Error("No contents to update");
		}
		const { element, options } = this.history[ this.history.length - 1 ];

		if (options.width !== null) {
			const targetWidth = options.width ?? (Number(element.getAttribute("data-modal-width")) || undefined) ?? this.defaultWidth;
			contents.style.width = `${targetWidth}px`;
			contents.style.maxWidth = `${targetWidth}px`;
		}

		const windowHeight = document.documentElement.clientHeight;
		const currentMarginTop = parseInt(contents.style.marginTop || "0", 10);
		const targetHeight = options.height ?? (Number(element.getAttribute("data-modal-height")) || undefined);
		const elementHeight = targetHeight ?? contents.offsetHeight;

		if (isNaN(parseFloat(String(marginTop)))) {
			marginTop = Math.max(10, (windowHeight - elementHeight) / 2);
		}

		contents.style.height = `${targetHeight}px`;
		contents.style.maxHeight = `${targetHeight}px`;

		if (force || (currentMarginTop + elementHeight) > windowHeight) {
			contents.style.marginTop = `${marginTop}px`;
		}
	}

	public emphasize(): void {
		this.view.element.classList.add("-emphasize");
		clearTimeout(this.emphasizeTimer);
		this.emphasizeTimer = setTimeout(() => {
			this.view.element.classList.remove("-emphasize");
		}, 1000);
	}
}
